import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  Paper,
  Text,
  Stack,
  Group,
  Button,
  ActionIcon,
  Alert,
  Center,
  RingProgress,
  ThemeIcon,
  Slider,
} from '@mantine/core';
import {
  IconMicrophone,
  IconPlayerStop,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
  IconAlertCircle,
} from '@tabler/icons-react';

interface VoiceRecorderProps {
  onRecordingComplete: (audio: Blob) => void;
  onRecordingCleared: () => void;
  maxDuration?: number; // in seconds, default 300
  disabled?: boolean;
  idleHint?: string;
}

type RecorderState = 'idle' | 'recording' | 'recorded';

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getSupportedMimeType = (): string => {
  const types = ['audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return 'audio/webm'; // fallback
};

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onRecordingCleared,
  maxDuration = 300,
  disabled = false,
  idleHint,
}) => {
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const isSeeking = useRef(false);
  const wasPlayingBeforeSeek = useRef(false);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);

  // Set up audio element when we have a recording.
  // Duration is already known from recordingStartTimeRef so we don't need
  // to resolve it from the audio element (WebM blobs often report Infinity).
  useEffect(() => {
    if (!audioUrl) {
      audioRef.current = null;
      return;
    }

    const audio = new Audio();
    audioRef.current = audio;

    audio.addEventListener('timeupdate', () => {
      if (!isSeeking.current) {
        setPlaybackTime(audio.currentTime);
      }
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setPlaybackTime(0);
    });

    audio.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    });

    audio.preload = 'auto';
    audio.src = audioUrl;

    return () => {
      audio.pause();
      audio.removeAttribute('src');
    };
  }, [audioUrl]);

  const startRecording = useCallback(async () => {
    setError(null);

    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError('Voice recording is not supported in this browser. Please try Chrome, Firefox, or Safari.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const recordedDuration = (Date.now() - recordingStartTimeRef.current) / 1000;
        setAudioBlob(blob);
        setDuration(recordedDuration);

        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        setState('recorded');
        onRecordingComplete(blob);

        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      mediaRecorder.start(1000);
      setState('recording');
      setElapsedTime(0);
      recordingStartTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => {
          const next = prev + 1;
          if (next >= maxDuration) {
            mediaRecorderRef.current?.stop();
            if (timerRef.current) clearInterval(timerRef.current);
          }
          return next;
        });
      }, 1000);
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone access was denied. Please allow microphone access in your browser settings and try again.');
      } else {
        setError('Could not access microphone. Please check your device settings.');
      }
    }
  }, [maxDuration, onRecordingComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [state]);

  const clearRecording = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsedTime(0);
    setDuration(0);
    setPlaybackTime(0);
    setIsPlaying(false);
    setState('idle');
    setError(null);
    onRecordingCleared();
  }, [audioUrl, onRecordingCleared]);

  const togglePlayback = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
  }, [isPlaying]);

  const handleSeekDrag = useCallback((value: number) => {
    if (!isSeeking.current) {
      // First drag event — pause playback
      isSeeking.current = true;
      wasPlayingBeforeSeek.current = isPlaying;
      if (audioRef.current && isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
    setPlaybackTime(value);
  }, [isPlaying]);

  const handleSeekCommit = useCallback((value: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = value;
    isSeeking.current = false;
    if (wasPlayingBeforeSeek.current) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
  }, []);

  const recordingProgress = (elapsedTime / maxDuration) * 100;

  const defaultHint = `Describe what you'd like to cook or what ingredients you have (max ${Math.floor(maxDuration / 60)} minutes).`;

  return (
    <Stack gap="md">
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Recording error"
          color="red"
          variant="light"
          radius="lg"
        >
          {error}
        </Alert>
      )}

      {/* Idle State - Start Recording */}
      {state === 'idle' && (
        <Paper
          withBorder
          p="xl"
          radius="lg"
          style={{
            borderStyle: 'dashed',
            borderColor: 'var(--mantine-color-gray-4)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={disabled ? undefined : startRecording}
        >
          <Center>
            <Stack align="center" gap="sm">
              <ThemeIcon
                size={60}
                radius="xl"
                variant="light"
                color="orange"
              >
                <IconMicrophone size={30} />
              </ThemeIcon>
              <Text fw={500} size="lg">
                {isTouchDevice ? 'Tap' : 'Click'} to start recording
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {idleHint || defaultHint}
              </Text>
            </Stack>
          </Center>
        </Paper>
      )}

      {/* Recording State */}
      {state === 'recording' && (
        <Paper withBorder radius="lg" p="xl">
          <Center>
            <Stack align="center" gap="md">
              <RingProgress
                size={100}
                thickness={6}
                roundCaps
                sections={[{ value: recordingProgress, color: 'red' }]}
                label={
                  <Center>
                    <Text fw={700} size="lg">
                      {formatTime(elapsedTime)}
                    </Text>
                  </Center>
                }
              />
              <Group gap="xs">
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-red-6)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
                <Text size="sm" c="red" fw={500}>
                  Recording...
                </Text>
              </Group>
              <Button
                color="red"
                leftSection={<IconPlayerStop size={16} />}
                onClick={stopRecording}
                size="md"
              >
                Stop Recording
              </Button>
            </Stack>
          </Center>
        </Paper>
      )}

      {/* Recorded State - Custom Audio Player */}
      {state === 'recorded' && audioUrl && (
        <Paper withBorder radius="lg" p="lg">
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text fw={500}>Recording Preview</Text>
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {audioBlob ? formatFileSize(audioBlob.size) : ''}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={clearRecording}
                  title="Delete recording"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>
            <Group gap="sm" align="center" wrap="nowrap">
              <ActionIcon
                variant="light"
                color="orange"
                size="lg"
                radius="xl"
                onClick={togglePlayback}
              >
                {isPlaying ? (
                  <IconPlayerPause size={18} />
                ) : (
                  <IconPlayerPlay size={18} style={{ marginLeft: 2 }} />
                )}
              </ActionIcon>
              <Text size="xs" c="dimmed" style={{ minWidth: 36, textAlign: 'right' }}>
                {formatTime(playbackTime)}
              </Text>
              <Slider
                value={playbackTime}
                onChange={handleSeekDrag}
                onChangeEnd={handleSeekCommit}
                min={0}
                max={duration || 1}
                step={0.1}
                label={null}
                size="sm"
                style={{ flex: 1 }}
                styles={{
                  thumb: { width: 12, height: 12 },
                }}
              />
              <Text size="xs" c="dimmed" style={{ minWidth: 36 }}>
                {formatTime(duration)}
              </Text>
            </Group>
          </Stack>
        </Paper>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Stack>
  );
};
